<template>
<div>
  <div class="title-bar title-h4">최근 30일 분석</div>
  <el-input class="ticket-input-class mr-b25" v-loading="analysisLoading" readonly resize="none" type="textarea" v-model="eventHistAnalysisInfo" :rows="3" ></el-input>
  <el-row type="flex" justify="space-between" class="title-bar">
    <span class="title-h4">최근 30일 이력</span>
    <w-header-search-button @click="showSearchTool = !showSearchTool"></w-header-search-button>
  </el-row>
  <el-table-wrapper
    ref="eventPastTable" v-loading="histLoading" style="width:100%;" border header-align="center"
    :data="eventTableData" :columns="eventTableColumns" pagination :total="total" :show-custom-header="true"
    :show-search-tool="showSearchTool" @condition-change="loadLastEventHist" remote>
    <template slot-scope="scope" slot="eventlevel-slot">
      <w-eventlevel-cell :eventlevel-id="scope.row.voEventlevel.eventlevelId"/>
    </template>
    <template slot-scope="scope" slot="trouble-ticket-group-id-slot">
      <span v-if="scope.row.voTroubleShooting == null">-</span>
      <span v-else class="link c-pointer" @click="showTroubleTicketPopup(scope.row.voTroubleShooting.troubleTicketGroupId)">{{scope.row.voTroubleShooting.troubleTicketGroupId}}</span>
    </template>
  </el-table-wrapper>
  <w-event-ticket-status-popup ref="ticketPopup" :trouble-ticket-info="troubleTicketInfo"></w-event-ticket-status-popup>
</div>
</template>

<script>
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { columnDefault } from '@/constants/table';
import msgUtil from '@/utils/message';
import { getDateToString, getDuration } from '@/utils/date';
import { EMPTY_TEXT } from '@/utils/string';
import EventCommonApi from '@/api/event/event-common';
import WEventlevelCell from '@/components/table/EventlevelCell';
import WEventTicketStatusPopup from '@/views/event/components/popup/EventTicketStatusPopup';

/**
 * 이벤트 과거 분석 화면
 */
export default {
  name: 'WEventPastAnalysisView',
  components: {
    WEventlevelCell,
    WEventTicketStatusPopup,
  },
  props: {
    eventHistData: {
      type: Object,
    },
  },
  data() {
    return {
      /* 최근 이벤트 분석 정보 */
      analysisLoading: false,
      eventHistAnalysisInfo: null,
      /* 최근 이벤트 이력 */
      histLoading: false,
      columnDefault,
      showSearchTool: false,
      total: 0,
      eventTableData: [],
      eventTableColumns: [
        {
          prop: 'eventlevelName',
          label: '등급',
          width: 80,
          scopedSlot: 'eventlevel-slot',
          searchable: true,
          searchType: 'eventlevel',
          columnKey: 'eventlevelId',
        },
        {
          prop: 'checkDatetime',
          label: '발생 시각',
          searchType: 'date',
          searchable: true,
          formatter: this.getDateFormatter,
        },
        {
          prop: 'releaseDatetime',
          label: '해제 시각',
          searchType: 'date',
          searchable: true,
          formatter: this.getDateFormatter,
        },
        {
          prop: 'eventMessage',
          label: '장애티켓',
          scopedSlot: 'trouble-ticket-group-id-slot',
        },
      ],
      /* 기타 */
      troubleTicketInfo: {},
    };
  },
  watch: {
    eventHistData: {
      handler(value) {
        if (isEmpty(value)) {
          this.reset();
        } else {
          this.loadData();
        }
      },
    },
  },
  methods: {
    getPastAnalysisParams() {
      const params = {
        hashkeyIdString: this.eventHistData.hashkeyIdString,
        hashkeyId: this.eventHistData.hashkeyIdString,
        startDatetime: moment(this.eventHistData.checkDatetime)
          .subtract(1, 'month')
          .valueOf(),
        endDatetime: this.eventHistData.checkDatetime,
      };
      return params;
    },
    /**
     * 데이터를 조회한다.
     * @author Ju
     */
    loadData() {
      const eventParams = {};
      const params = this.getPastAnalysisParams();
      this.loadEventHistAnalysisInfoForLast30Days(params);
      this.loadLastEventHist();
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.analysisLoading = false;
      this.histLoading = false;
      this.eventHistAnalysisInfo = null;
      this.eventTableData = [];
    },
    /**
     * 이벤트의 최근 30일 동안의 분석 정보를 조회한다.
     * @author Ju
     * @param  {Object}  params
     */
    loadEventHistAnalysisInfoForLast30Days(params) {
      this.analysisLoading = true;
      EventCommonApi.getEventHistListByHashkeyIdLast30Days(params)
        .then((response) => {
          if (response.data.success) {
            const lastEventCheckDatetime = this.getDateFormatter(null, null, response.data.data.lastEventCheckDatetime);
            const avgDiffTime = getDuration(response.data.data.avgDiffTime);
            const eventCount = response.data.data.eventCount;
            const eventHistAnalysisInfo = `동일 이벤트 직전 발생 일시 : ${lastEventCheckDatetime}\n동일 이벤트 발생 횟수 (최근 30일 기준) : ${eventCount}회 (평균 ${avgDiffTime} 간격)`;
            this.eventHistAnalysisInfo = eventHistAnalysisInfo;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '최근 30일 이벤트 분석 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '최근 30일 이벤트 분석 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.analysisLoading = false;
        });
    },
    /**
     * 최근 이벤트 이력 정보를 조회한다.
     * @author Ju
     * @param  {Object}  params
     */
    loadLastEventHist() {
      this.histLoading = true;
      const eventHistParams = {};
      const params = this.getPastAnalysisParams();
      const tableParams = this.$refs.eventPastTable.getParameter();
      Object.assign(eventHistParams, tableParams, params);
      EventCommonApi.getEventHistListByHashkeyIdLast30DaysList(eventHistParams)
        .then((response) => {
          if (response.data.success) {
            this.total = response.data.total;
            this.eventTableData = response.data.data;
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '최근 이벤트 이력 정보', false));
          }
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '최근 이벤트 이력 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.histLoading = false;
        });
    },
    /**
     * 이벤트의 장애 티켓 상세 팝업을 연다.
     * @author Ju
     * @param  {[type]} row [description]
     */
    showTroubleTicketPopup(troubleTicketGroupId) {
      this.troubleTicketInfo = {
        troubleTicketGroupId,
      };
      this.$refs.ticketPopup.open();
    },
    /**
     * 날짜 데이터 포맷
     * @author Ju
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/popup/EventSmartAnalyzerPopup/EventPastAnalysisView.vue